/**
 * Firmware upgrade orchestration aligned with oldcode/upgrading.html.
 */

import type { FirmwareTab, UpgradeCallbacks } from './firmware-types'
import { buildFirmwareUrl } from './firmware-mock-data'
import {
  getEnterUpgradePacketForTab,
  getRequiredConnectionTypeByTab,
  isNormalModeDevice,
  isUpgradeModeDevice,
  needsDeviceReconnect,
  openDeviceIfNeeded,
  resolveConnectionTypeFromDevices,
  shouldUseSbtOta,
  sleep,
  waitForUpgradeDevice,
} from './firmware-device'
import { startSimpleUpgrade, cancelSimpleUpgrade, loadFirmwareFile } from './simple-firmware-upgrade'
import { startSbtOtaUpgrade, cancelSbtOtaUpgrade } from './sbt-ota-upgrade'

export type { FirmwareTab, UpgradeCallbacks }

type ConnectionType = 'USB' | '2.4G' | ''

interface ConnectionValidation {
  ok: boolean
  actualType: ConnectionType
  requiredType: 'USB' | '2.4G'
  sourceDevice: HIDDevice | null
  message?: string
}

interface UpgradeOptions {
  firmwareFile?: File
  firmwareUrl?: string
  hidDevice?: HIDDevice | null
}

let hasTriggeredEnterUpgrade = false

export async function fetchFirmwareFromUrl(url: string): Promise<Uint8Array> {
  const ts = Date.now()
  const sep = url.includes('?') ? '&' : '?'
  const res = await fetch(`${url}${sep}_t=${ts}`, { cache: 'no-store' })
  if (!res.ok) {
    throw new Error(`Firmware download failed: ${res.status} ${res.statusText}`)
  }

  const firmware = new Uint8Array(await res.arrayBuffer())
  if (!firmware.length) throw new Error('Firmware file is empty')
  return firmware
}

export function validateConnectionForTab(
  tab: FirmwareTab,
  devices: HIDDevice[],
  preferredDevice?: HIDDevice | null
): ConnectionValidation {
  const requiredType = getRequiredConnectionTypeByTab(tab)
  const { actualType, sourceDevice } = resolveConnectionTypeFromDevices(devices, preferredDevice)

  if (!actualType) {
    return {
      ok: false,
      actualType,
      requiredType,
      sourceDevice,
      message: 'Unable to identify connection type. Please confirm the device is connected correctly.',
    }
  }

  return {
    ok: actualType === requiredType,
    actualType,
    requiredType,
    sourceDevice,
    message:
      actualType === requiredType
        ? undefined
        : `${tab} upgrade requires ${requiredType}, current connection is ${actualType}.`,
  }
}

function getUpgradeTargetForTab(_tab: FirmwareTab): number {
  return 0
}

async function enterUpgradeModeIfPossible(
  normalDevice: HIDDevice,
  tab: FirmwareTab,
  onStatus?: (msg: string) => void
): Promise<boolean> {
  if (!normalDevice || hasTriggeredEnterUpgrade) return false

  const packet = getEnterUpgradePacketForTab(tab)
  if (!packet) return false

  hasTriggeredEnterUpgrade = true

  try {
    await openDeviceIfNeeded(normalDevice)
    onStatus?.('Sending enter-upgrade command...')
    const fullPacket = new Uint8Array(63)
    fullPacket.set(packet)
    await normalDevice.sendReport(49, fullPacket)
    onStatus?.('Command sent. Waiting for upgrade-mode device...')
    return true
  } catch {
    hasTriggeredEnterUpgrade = false
    return false
  }
}

async function ensureUpgradeDevice(
  tab: FirmwareTab,
  onStatus?: (msg: string) => void,
  preferredDevice?: HIDDevice | null
): Promise<HIDDevice | null> {
  if (!navigator.hid) return null

  const devices = await navigator.hid.getDevices()
  let upgradeDevice: HIDDevice | undefined =
    preferredDevice && isUpgradeModeDevice(preferredDevice) ? preferredDevice : undefined
  let normalDevice: HIDDevice | undefined =
    preferredDevice && isNormalModeDevice(preferredDevice) ? preferredDevice : undefined

  for (const dev of devices) {
    if (!upgradeDevice && isUpgradeModeDevice(dev)) upgradeDevice = dev
    if (!normalDevice && isNormalModeDevice(dev)) normalDevice = dev
  }

  if (!needsDeviceReconnect(tab)) {
    if (!normalDevice) return null
    await openDeviceIfNeeded(normalDevice)
    return normalDevice
  }

  if (!upgradeDevice && normalDevice) {
    hasTriggeredEnterUpgrade = false
    const didSend = await enterUpgradeModeIfPossible(normalDevice, tab, onStatus)
    if (!didSend) return null
    await sleep(2000)
    upgradeDevice = (await waitForUpgradeDevice(15000)) ?? undefined
  }

  if (!upgradeDevice) return null
  await openDeviceIfNeeded(upgradeDevice)
  return upgradeDevice
}

/**
 * Execute one full firmware upgrade.
 *
 * Device rules are intentionally the same as oldcode/upgrading.html:
 * - earphone and dongle use simplified OTA on reportId 0 after reconnecting to upgrade mode.
 * - earphoneNoiseCancel and earphoneBluetooth stay on reportId 49 and let SBT OTA send 0x85/0x86
 *   after listeners are registered.
 */
export async function runFirmwareUpgrade(
  tab: FirmwareTab,
  version: string,
  callbacks: UpgradeCallbacks = {},
  options: UpgradeOptions = {}
): Promise<void> {
  hasTriggeredEnterUpgrade = false

  const onStatus = (msg: string) => callbacks.onLog?.(msg, 'info')

  if (!navigator.hid) {
    throw new Error('Current browser does not support WebHID')
  }

  const devices = await navigator.hid.getDevices()
  const connCheck = validateConnectionForTab(tab, devices, options.hidDevice)
  if (!connCheck.ok) {
    throw new Error(connCheck.message || 'Connection type mismatch')
  }

  onStatus('Detecting device...')
  const hidDevice = await ensureUpgradeDevice(tab, onStatus, options.hidDevice)
  if (!hidDevice) {
    throw new Error('No available upgrade device found. Please reconnect the device and try again.')
  }

  onStatus('Downloading firmware...')
  let firmware: Uint8Array
  if (options.firmwareFile) {
    firmware = await loadFirmwareFile(options.firmwareFile)
  } else {
    const url = options.firmwareUrl || buildFirmwareUrl(tab, version)
    if (!url) throw new Error('Invalid firmware URL')
    firmware = await fetchFirmwareFromUrl(url)
  }

  onStatus('Upgrading. Keep the device connected...')

  const upgradeCallbacks: UpgradeCallbacks = {
    onProgress: callbacks.onProgress,
    onComplete: callbacks.onComplete,
    onError: callbacks.onError,
    onLog: callbacks.onLog,
  }

  if (shouldUseSbtOta(tab)) {
    const enterCmd = getEnterUpgradePacketForTab(tab)
    await startSbtOtaUpgrade(
      hidDevice,
      firmware,
      upgradeCallbacks,
      getUpgradeTargetForTab(tab),
      {},
      enterCmd
    )
  } else {
    await startSimpleUpgrade(hidDevice, firmware, upgradeCallbacks)
  }
}

export function cancelFirmwareUpgrade(): void {
  cancelSimpleUpgrade()
  cancelSbtOtaUpgrade()
  hasTriggeredEnterUpgrade = false
}

export async function pickHidDeviceManually(vendorId?: number): Promise<HIDDevice | null> {
  if (!navigator.hid) return null
  const filters = vendorId != null ? [{ vendorId }] : []
  const picked = await navigator.hid.requestDevice({ filters })
  return picked.find(dev => isUpgradeModeDevice(dev)) || picked[0] || null
}
